<template>
	<header ref="el" data-component="header">
		<div class="top-content g-row">
			<h2 class="-title-1 g-col xxl-12 lg-14">{{ title }}</h2>

			<div v-if="isMobile" class="mobile-top-wrapper">
				<span class="-text-3 g-col xxl-offset-12 xxl-4 sm-offset-0 sm-14 description-label">{{ descriptionLabel }}</span>
				<div class="g-col xxl-offset-12 sm-offset-0 sm-12 xxl-3">
					<h3 class="-text-3 description">{{ description }}</h3>
					<ButtonSimple :label="cta.buttonLabel" :link="cta.to" />
				</div>
			</div>

			<template v-else>
				<span class="-text-3 g-col xxl-offset-12 xxl-4 sm-offset-0 sm-14 description-label">{{ descriptionLabel }}</span>

				<div ref="lineRef" class="g-col xxl-16 separator-container">
					<span class="separator" />
				</div>

				<div class="g-col xxl-offset-12 sm-offset-0 sm-12 xxl-3">
					<h3 class="-text-3 description">{{ description }}</h3>
					<ButtonSimple :label="cta.buttonLabel" :link="cta.to" />
				</div>
			</template>
		</div>

		<div class="bottom-content g-row">
			<span class="-text-3 g-col xxl-offset-12 xxl-2">{{ bottomNote }}</span>
		</div>

		<div class="shader-container" data-shader="gold-header">
			<div class="top-container">
				<div class="corner-overlay top-left"></div>
				<div class="corner-overlay top-right"></div>
				<div class="corner-overlay bottom-left"></div>
				<div class="corner-overlay bottom-right"></div>
			</div>

			<div class="mask-spacer"></div>

			<div class="bottom-container">
				<div class="corner-overlay top-left"></div>
				<div class="corner-overlay top-right"></div>
				<div class="corner-overlay bottom-left"></div>
				<div class="corner-overlay bottom-right"></div>

				<div class="section-1 xxl-10"></div>
				<HeaderDivider2SVG class="divider-2" />
				<div class="section-2 xxl-2"></div>
				<HeaderDivider1SVG class="divider-1" />
			</div>
		</div>

		<div class="arrow-container">
			<Icon slug="arrow" size="s" @click="handleArrowClick" />
		</div>
	</header>
</template>

<script setup>
import { gsap, CustomEase } from 'gsap/all'
import SplitText from 'gsap/SplitText'

import { Icon } from '~/components/core'
import { ButtonSimple } from '~/components/primitives'

import { useScreen } from 'vue-screen'
import { breakpoints } from '~/config'

import HeaderDivider1SVG from '~/public/imgs/header/header_divider_1.svg'
import HeaderDivider2SVG from '~/public/imgs/header/header_divider_2.svg'

const props = defineProps({
	title: { type: String, default: undefined },
	descriptionLabel: { type: String, default: undefined },
	description: { type: String, default: undefined },
	bottomNote: { type: String, default: undefined },
	cta: { type: Object, default: undefined },
})

import.meta.client && gsap.registerPlugin(SplitText, CustomEase)

const el = ref(null)
const lineRef = ref(null)

const context = inject('pageContext')
const scroller = inject('$scroller')

const screen = useScreen()
const isMobile = ref(null)

let split = null

const handleArrowClick = () => {
	if (scroller && typeof window !== 'undefined') scroller.value.scrollTo(window.innerHeight)
}

watch(
	() => screen?.width,
	(width) => {
		isMobile.value = width < breakpoints.small
	}
)

const setup = async () => {
	isMobile.value = screen.width < breakpoints.small
	await nextTick()

	const titleEl = el.value.querySelector('h2')

	if (titleEl) {
		split = new SplitText(titleEl, { type: 'words', wordsClass: 'word-target' })

		split.words.forEach((word) => {
			const wrapper = document.createElement('span')
			wrapper.classList.add('word-mask')
			wrapper.style.display = 'inline-block'
			wrapper.style.overflow = 'hidden'
			wrapper.style.verticalAlign = 'bottom'

			wrapper.style.paddingBottom = '5px'
			wrapper.style.marginBottom = '-5px'
			wrapper.style.paddingRight = '5px'
			wrapper.style.marginRight = '-5px'

			word.parentNode.insertBefore(wrapper, word)
			wrapper.appendChild(word)

			const space = document.createTextNode(' ')
			wrapper.parentNode.insertBefore(space, wrapper.nextSibling)
		})

		init()
	}
}
const init = () => {
	if (lineRef.value) {
		gsap.fromTo(
			lineRef.value,
			{
				scaleX: 0,
				transformOrigin: 'left',
			},
			{
				scaleX: 1,
				duration: 1.8,
				ease: 'expo.inOut',
			}
		)
	}

	gsap.fromTo(
		split.words,
		{
			y: '105%',
		},
		{
			y: '0%',
			stagger: 0.07,
			duration: 0.9,
			ease: CustomEase.create('custom', 'M0,0 C0.7,0 0,1 1,1 '),
			//ease: CustomEase.create('custom', 'M0,0 C0.17,0 0.286,0.085 0.32,0.115 0.394,0.18 0.468,0.302 0.5,0.5 0.532,0.703 0.608,0.816 0.645,0.852 0.67,0.877 0.794,1 1,1 '),
			delay: 0.2,
		}
	)

	if (el.value.querySelector('.description')) {
		gsap.fromTo(
			el.value.querySelector('.description'),
			{
				x: isMobile.value ? 10 : -10,
				opacity: 0,
			},
			{
				opacity: 1,
				x: 0,
				duration: 1.5,
				ease: 'expo.out',
				delay: 1.0,
			}
		)
	}

	if (el.value.querySelector('button')) {
		gsap.fromTo(
			el.value.querySelector('button'),
			{
				x: isMobile.value ? 10 : -10,
				opacity: 0,
			},
			{
				opacity: 1,
				x: 0,
				duration: 1.5,
				ease: 'expo.out',
				delay: 1.0,
			}
		)
	}

	if (el.value.querySelector('.description-label')) {
		gsap.fromTo(
			el.value.querySelector('.description-label'),
			{
				x: isMobile.value ? 10 : -10,
				opacity: 0,
			},
			{
				opacity: 0.4,
				x: 0,
				duration: 1.5,
				ease: 'expo.out',
				delay: 0.9,
			}
		)
	}

	if (el.value.querySelector('.bottom-content')) {
		gsap.fromTo(
			el.value.querySelector('.bottom-content'),
			{
				y: -5,
				opacity: 0,
			},
			{
				y: 0,
				opacity: 1,
				duration: 1.0,
				ease: 'expo.out',
				delay: 1.1,
			}
		)
	}

	if (el.value.querySelector('.arrow-container')) {
		gsap.fromTo(
			el.value.querySelector('.arrow-container'),
			{
				opacity: 0,
				y: -5,
			},
			{
				opacity: 1,
				y: 0,
				duration: 1.0,
				ease: 'expo.out',
				delay: 1.2,
			}
		)
	}
}
const kill = () => {}

onBeforeUnmount(() => {})
onMounted(() => {
	context.$page.loader.ready.then(() => setup())
})
</script>

<style lang="scss" scoped>
[data-component='header'] {
	--icon-color: var(--color-geral-creme);

	overflow: hidden;

	position: relative;
	height: 100svh;
	width: 100%;

	.title-wrapper {
		display: flex;
		flex-wrap: wrap;
	}

	:deep(.word-target) {
		display: inline-block;
		transform: translateY(100%);
	}

	.top-content {
		position: relative;
		z-index: 1;

		padding-top: rs(60, 60);

		@include max-width($sm) {
			padding-top: var(--grid-margin);
			padding-bottom: rs(160, 160);

			display: flex;
			flex-direction: column;
			justify-content: space-between;
			min-height: 100%;
		}

		@include max-width($xxs) {
			padding-bottom: rs(130, 130);
		}

		.mobile-top-wrapper {
			height: fit-content;
		}

		.description-label {
			opacity: 0.4;
			padding-top: rs(12, 12);

			@include max-width($sm) {
				padding-bottom: rs(12, 12);
			}
		}

		.separator-container {
			@include max-width($sm) {
				display: none;
			}

			.separator {
				margin-top: rs(8, 8);
				margin-bottom: rs(16, 16);

				width: 100%;

				border-top: 1px solid var(--color-geral-white);
				opacity: 0.1;
			}
		}
	}

	.bottom-content {
		position: absolute;

		bottom: 24px;
		height: 168px;

		align-items: center;

		@include max-width($sm) {
			display: none;
		}

		@include max-width($lg) {
			height: rs(120, 120);
		}

		@include max-width($xxs) {
			height: rs(90, 90);
		}
	}

	.arrow-container {
		position: absolute;
		display: flex;

		height: 168px;

		align-items: center;
		justify-content: flex-end;

		margin-bottom: rs(12, 12);

		right: 70px;
		bottom: 0;

		z-index: 1;

		// @include max-width($xl) {
		// 	right: 68px;
		// }

		@include max-width($lg) {
			height: rs(120, 120);
			right: 60px;
		}

		@include max-width($sm) {
			height: rs(120, 120);
			margin-bottom: 0;
			right: 36px;
			bottom: -12px;
		}

		@include max-width($xxs) {
			height: rs(90, 90);
			right: 26px;
			bottom: -8px;
		}

		:deep([data-component='icon']) {
			cursor: pointer;
			@include extend_hitbox(20);
		}
	}

	.shader-container {
		position: absolute;

		top: 24px;
		left: 24px;
		right: 24px;
		bottom: 24px;

		display: flex;
		flex-direction: column;

		@include max-width($sm) {
			top: 0;
			bottom: 0;
			left: 0;
			right: 0;
		}
	}

	.mask-spacer {
		width: 102%;
		transform: translateX(-1%);
		height: rs(12, 6);
		background-color: var(--color-geral-brown);
		flex-shrink: 0;
		z-index: 1;
	}

	.top-container,
	.bottom-container {
		position: relative;
		width: 100%;

		.corner-overlay {
			position: absolute;
			width: 14px;
			height: 14px;

			$radius: 12px;
			$color: var(--color-geral-brown);

			// @if $radius == 12px {
			// 	$radius: 70.8%;
			// }

			&.top-left {
				top: -2px;
				left: -2px;
				background: radial-gradient(circle at 100% 100%, transparent $radius, $color $radius);
			}

			&.top-right {
				top: -2px;
				right: -2px;
				background: radial-gradient(circle at 0% 100%, transparent $radius, $color $radius);
			}

			&.bottom-left {
				bottom: -2px;
				left: -2px;
				background: radial-gradient(circle at 100% 0%, transparent $radius, $color $radius);
			}

			&.bottom-right {
				bottom: -2px;
				right: -2px;
				background: radial-gradient(circle at 0% 0%, transparent $radius, $color $radius);
			}
		}
	}

	.top-container {
		flex: 1;
	}

	.bottom-container {
		height: 168px;
		display: flex;
		flex-direction: row;
		justify-content: space-between;

		@include max-width($sm) {
			height: rs(120, 120);
		}

		@include max-width($lg) {
			height: rs(120, 120);
		}

		@include max-width($xxs) {
			height: rs(90, 90);
		}

		.section-1,
		.section-2 {
			position: relative;
			height: 100%;
		}

		.section-1 {
			flex-shrink: 1;
			min-width: 0;
		}

		.section-2 {
			flex-shrink: 0;
		}

		.divider-1,
		.divider-2 {
			height: 169px;
			flex-shrink: 0;

			transform: translateY(-0.5px);

			@include max-width($sm) {
				height: rs(122, 122);
			}

			@include max-width($lg) {
				height: rs(122, 122);
			}

			@include max-width($xxs) {
				height: rs(92, 92);
			}

			:deep(path) {
				fill: var(--color-geral-brown);
			}
		}

		.divider-1 {
			padding-right: rs(14, 14);
		}

		.divider-2 {
			@include max-width($sm) {
				display: none;
			}
		}
	}

	.description {
		white-space: pre-line;
		padding-bottom: rs(32, 32);

		@include max-width($sm) {
			padding-bottom: rs(24, 24);
		}
	}
}
</style>
